/**
 * Validation utilities for CCMS components
 */

export interface ValidationRule<T = any> {
  validate: (value: T) => boolean;
  message: string;
}

export interface FieldValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: ValidationRule[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate a field value against a set of rules
 */
export function validateField(value: any, rules: FieldValidation): ValidationResult {
  const errors: string[] = [];

  if (rules.required && !value) {
    errors.push('This field is required');
  }

  if (rules.minLength && typeof value === 'string' && value.length < rules.minLength) {
    errors.push(`Minimum length is ${rules.minLength} characters`);
  }

  if (rules.maxLength && typeof value === 'string' && value.length > rules.maxLength) {
    errors.push(`Maximum length is ${rules.maxLength} characters`);
  }

  if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
    errors.push('Invalid format');
  }

  if (rules.custom) {
    for (const rule of rules.custom) {
      if (!rule.validate(value)) {
        errors.push(rule.message);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Common validation patterns
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_REGEX = /^\+?[\d\s-()]+$/;
export const URL_REGEX = /^https?:\/\/.+/;
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validate email address
 */
export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

/**
 * Validate phone number
 */
export function isValidPhone(phone: string): boolean {
  return PHONE_REGEX.test(phone);
}

/**
 * Validate URL
 */
export function isValidUrl(url: string): boolean {
  return URL_REGEX.test(url);
}

/**
 * Validate UUID
 */
export function isValidUUID(uuid: string): boolean {
  return UUID_REGEX.test(uuid);
}

/**
 * Validate file MIME type against allowed types.
 *
 * Accepts:
 *  - `*\/*` — wildcard for any file (the `accept` default the uploader passes
 *    when no restriction is set). Naively splitting this would yield `*` as
 *    the category and reject everything since no MIME type starts with `*\/`.
 *  - `image\/*` style category wildcards
 *  - exact MIME types like `application/pdf`
 *
 * An empty `allowedTypes` array is treated as "no restriction" — matches the
 * caller's intent of "any file" without forcing them to pass `['*\/*']`.
 */
export function validateMimeType(file: File, allowedTypes: string[]): boolean {
  if (!allowedTypes || allowedTypes.length === 0) return true;
  return allowedTypes.some((type) => {
    if (type === '*/*' || type === '*') return true;
    if (type.endsWith('/*')) {
      const category = type.split('/')[0];
      return file.type.startsWith(category + '/');
    }
    return file.type === type;
  });
}

/**
 * Validate file size
 */
export function validateFileSize(file: File, maxSizeInBytes: number): boolean {
  return file.size <= maxSizeInBytes;
}
